import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { createEvent, updateEvent, fetchEvent } from '../../store/slices/eventSlice';
import { addNotification } from '../../store/slices/uiSlice';
import SortableSection from './SortableSection';
import formService from '../../services/formService';
import CourseEnrollmentModal from './CourseEnrollmentModal';

const EventModal = ({ event, isCreateMode, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Form state
  const [formData, setFormData] = useState({
    event_name: '',
    event_type: 'event',
    venue: '',
    remark: '',
    main_lecturer: '',
    default_quantity: 0,
    form_id: null,
    sections: []
  });
  const [forms, setForms] = useState([]);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [enrollmentSectionId, setEnrollmentSectionId] = useState('');
  // After first save on create, keep modal open so user can manage participant lists
  const [workingEvent, setWorkingEvent] = useState(event && !isCreateMode ? event : null);
  const effectiveIsCreate = !workingEvent?.id;
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const applyEventToForm = (eventData) => {
    const sectionsWithIds = eventData.sections?.map((section, index) => ({
      ...section,
      id: section.id || `temp-${Date.now()}-${index}`,
      sort_order: section.sort_order ?? index,
      quantity: section.quantity ?? 0,
      form_id: section.form_id || null
    })) || [];

    setFormData({
      event_name: eventData.event_name || '',
      event_type: eventData.event_type || 'event',
      venue: eventData.venue || '',
      remark: eventData.remark || '',
      main_lecturer: eventData.main_lecturer || '',
      default_quantity: eventData.default_quantity || 0,
      form_id: eventData.form_id || null,
      sections: sectionsWithIds
    });
  };

  useEffect(() => {
    formService.list().then(setForms).catch(()=>{});

    if (event && !isCreateMode) {
      setWorkingEvent(event);
      if (event.id) {
        dispatch(fetchEvent(event.id)).then((result) => {
          if (result.payload) {
            applyEventToForm(result.payload);
            setWorkingEvent(result.payload);
          }
        });
      }
    } else {
      setWorkingEvent(null);
      // Reset form for create mode with one default section
      setFormData({
        event_name: '',
        event_type: event?.event_type || 'event',
        venue: '',
        remark: '',
        main_lecturer: '',
        default_quantity: 0,
        form_id: null,
        sections: [{
          id: `temp-${Date.now()}-0`,
          section_name: '',
          lecturer: '',
          sort_order: 0,
          quantity: 0,
          form_id: null,
          start_at: '',
          end_at: ''
        }]
      });
    }
  }, [event, isCreateMode, dispatch]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSectionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
      )
    }));
    
    // Clear section errors
    if (errors[`sections.${index}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`sections.${index}.${field}`]: ''
      }));
    }
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, {
        id: `temp-${Date.now()}-${prev.sections.length}`,
        section_name: '',
        lecturer: '',
        sort_order: prev.sections.length,
        quantity: 0,
        form_id: null,
        start_at: '',
        end_at: ''
      }]
    }));
  };

  // Drag and drop handler
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.sections.findIndex(section => section.id === active.id);
        const newIndex = prev.sections.findIndex(section => section.id === over.id);
        
        const newSections = arrayMove(prev.sections, oldIndex, newIndex);
        
        // Update sort_order for all sections
        const sectionsWithOrder = newSections.map((section, index) => ({
          ...section,
          sort_order: index
        }));

        return {
          ...prev,
          sections: sectionsWithOrder
        };
      });
    }
  };

  const removeSection = (index) => {
    if (formData.sections.length > 1) {
      setFormData(prev => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.event_name.trim()) {
      newErrors.event_name = 'Event name is required';
    }

    // Validate sections
    formData.sections.forEach((section, index) => {
      if (!section.start_at) {
        newErrors[`sections.${index}.start_at`] = 'Start time is required';
      }
      if (!section.end_at) {
        newErrors[`sections.${index}.end_at`] = 'End time is required';
      }
      if (section.start_at && section.end_at && new Date(section.start_at) >= new Date(section.end_at)) {
        newErrors[`sections.${index}.end_at`] = 'End time must be after start time';
      }
    });

    if (formData.sections.length === 0) {
      newErrors.sections = 'At least one section is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Persist create/update. Returns saved event detail or null.
   * @param {{ stayOpen?: boolean, force?: boolean, sectionIndex?: number }} options
   */
  const saveEvent = async ({ stayOpen = false, force = false } = {}) => {
    if (!validateForm()) {
      return null;
    }

    const sectionsData = formData.sections.map((section) => {
      const sectionId = typeof section.id === 'number' ? section.id
        : (typeof section.id === 'string' && !section.id.startsWith('temp-') ? parseInt(section.id, 10) : null);
      return {
        id: !isNaN(sectionId) ? sectionId : null,
        section_name: section.section_name || null,
        lecturer: section.lecturer || null,
        sort_order: section.sort_order || 0,
        quantity: section.quantity || 0,
        form_id: section.form_id || null,
        start_at: new Date(section.start_at).toISOString(),
        end_at: new Date(section.end_at).toISOString(),
      };
    });

    const eventData = {
      event_name: formData.event_name.trim(),
      event_type: formData.event_type,
      venue: formData.venue.trim() || null,
      remark: formData.remark.trim() || null,
      main_lecturer: formData.main_lecturer.trim() || null,
      default_quantity: formData.default_quantity || 0,
      form_id: formData.form_id || null,
      sections: sectionsData,
    };

    setLoading(true);
    try {
      let detail = null;
      if (effectiveIsCreate) {
        const created = await dispatch(createEvent(eventData)).unwrap();
        const createdId = created?.id || created?.data?.id;
        if (!createdId) throw new Error('Missing created event id');
        detail = await dispatch(fetchEvent(createdId)).unwrap();
        applyEventToForm(detail);
        setWorkingEvent(detail);
        dispatch(addNotification({
          type: 'success',
          title: t('common.successTitle'),
          message: t('events.notifications.createSuccess'),
        }));
      } else {
        const originalEvent = workingEvent || event;
        const hasBasicChanges =
          formData.event_name !== originalEvent.event_name ||
          formData.event_type !== (originalEvent.event_type || 'event') ||
          formData.venue !== (originalEvent.venue || '') ||
          formData.remark !== (originalEvent.remark || '') ||
          formData.main_lecturer !== (originalEvent.main_lecturer || '') ||
          formData.default_quantity !== (originalEvent.default_quantity || 0) ||
          (formData.form_id || null) !== (originalEvent.form_id || null);
        const hasSectionChanges = JSON.stringify(formData.sections) !== JSON.stringify(originalEvent.sections || []);

        if (!force && !hasBasicChanges && !hasSectionChanges) {
          detail = workingEvent;
          if (!stayOpen) {
            dispatch(addNotification({
              type: 'info',
              title: t('events.confirmations.noChangesTitle'),
              message: t('events.confirmations.noChangesMsg'),
            }));
          }
        } else {
          await dispatch(updateEvent({ id: workingEvent.id, eventData })).unwrap();
          detail = await dispatch(fetchEvent(workingEvent.id)).unwrap();
          applyEventToForm(detail);
          setWorkingEvent(detail);
          dispatch(addNotification({
            type: 'success',
            title: t('common.successTitle'),
            message: t('events.notifications.updateSuccess'),
          }));
        }
      }

      if (onSuccess) onSuccess();
      if (!stayOpen) onClose();
      return detail;
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: error.message || t('events.notifications.error', {
          action: effectiveIsCreate ? t('events.actions.create').toLowerCase() : t('events.actions.manage').toLowerCase(),
        }),
      }));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openEnrollmentForSection = async (section, sectionIndex) => {
    // Always allow from create/edit; auto-save first if needed
    let detail = workingEvent;
    const needsSave =
      !detail?.id ||
      typeof section?.id !== 'number' ||
      formData.sections.some((s) => typeof s.id !== 'number');

    if (needsSave) {
      detail = await saveEvent({ stayOpen: true, force: true });
      if (!detail?.id) return;
    }

    const sections = detail.sections || [];
    let targetSection = null;
    if (typeof sectionIndex === 'number' && sections[sectionIndex]) {
      targetSection = sections[sectionIndex];
    } else if (typeof section?.id === 'number') {
      targetSection = sections.find((s) => s.id === section.id) || null;
    }
    if (!targetSection && sections.length > 0) {
      targetSection = sections[0];
    }
    if (!targetSection?.id) {
      dispatch(addNotification({
        type: 'error',
        message: '找不到可管理的場次，請先儲存活動',
      }));
      return;
    }

    setEnrollmentSectionId(targetSection.id);
    setShowEnrollmentModal(true);
  };

  const handleManageSectionEnrollment = (section, index) => {
    openEnrollmentForSection(section, index);
  };

  const handleOpenParticipantsFromHeader = () => {
    const first = formData.sections[0];
    openEnrollmentForSection(first, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveEvent({ stayOpen: effectiveIsCreate });
  };

  // Note: formatDateTimeForInput function removed as we now use DatePicker components

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-4 gap-3">
            <h3 className="text-lg font-medium text-gray-900">
              {effectiveIsCreate ? t('events.modal.createTitle') : t('events.modal.editTitle')}
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleOpenParticipantsFromHeader}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-medium px-3 py-1.5 rounded"
              >
                參加者名單
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Event Information */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">{t('events.modal.eventInfo')}</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="event_name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('events.modal.eventName')}
                  </label>
                  <input
                    type="text"
                    id="event_name"
                    name="event_name"
                    value={formData.event_name}
                    onChange={handleFormChange}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 ${
                      errors.event_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('events.modal.eventNamePlaceholder')}
                  />
                  {errors.event_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.event_name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="event_type" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('events.modal.eventType')}
                  </label>
                  <select
                    id="event_type"
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                  >
                    <option value="worship">{t('events.eventTypes.worship')}</option>
                    <option value="course">{t('events.eventTypes.course')}</option>
                    <option value="cell_group">{t('events.eventTypes.cellGroup')}</option>
                    <option value="meeting">{t('events.eventTypes.meeting')}</option>
                    <option value="event">{t('events.eventTypes.event')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="venue" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('events.modal.venue')}
                  </label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={t('events.modal.venuePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="main_lecturer" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('events.modal.mainLecturer')}
                    <span className="text-sm text-gray-500 ml-1">{t('events.modal.mainLecturerNote')}</span>
                  </label>
                  <input
                    type="text"
                    id="main_lecturer"
                    name="main_lecturer"
                    value={formData.main_lecturer}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={t('events.modal.mainLecturerPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="default_quantity" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('events.modal.defaultQuantity')}
                    <span className="text-sm text-gray-500 ml-1">{t('events.modal.defaultQuantityNote')}</span>
                  </label>
                  <input
                    type="number"
                    id="default_quantity"
                    name="default_quantity"
                    min="0"
                    value={formData.default_quantity}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={t('events.modal.defaultQuantityPlaceholder')}
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('events.modal.attachForm')}</label>
                  <div className="flex items-center gap-2">
                    <select
                      name="form_id"
                      value={formData.form_id || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    >
                        <option value="">{t('events.modal.attachFormNone')}</option>
                      {forms.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    {formData.form_id && (
                      <div className="flex gap-1 mt-1">
                          <a className="text-xs px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded" href={`/forms/${formData.form_id}/edit`} target="_blank" rel="noreferrer">{t('events.modal.attachFormEdit')}</a>
                          <a className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded" href={`/forms/${formData.form_id}/submissions`} target="_blank" rel="noreferrer">{t('events.modal.attachFormResults')}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="remark" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('events.modal.remark')}
                </label>
                <textarea
                  id="remark"
                  name="remark"
                  rows={3}
                  value={formData.remark}
                  onChange={handleFormChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder={t('events.modal.remarkPlaceholder')}
                />
              </div>
            </div>

            {/* Sections/Classes */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-md font-medium text-gray-900">{t('events.modal.sectionsTitle')}</h4>
                <button
                  type="button"
                  onClick={addSection}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  {t('events.modal.addSection')}
                </button>
              </div>

              {errors.sections && (
                <p className="mb-3 text-sm text-red-600">{errors.sections}</p>
              )}

              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={formData.sections.map(section => section.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {formData.sections.map((section, index) => (
                      <SortableSection
                        key={section.id}
                        section={section}
                        index={index}
                        onSectionChange={handleSectionChange}
                        onRemoveSection={removeSection}
                        canRemove={formData.sections.length > 1}
                        errors={errors}
                        forms={forms}
                        t={t}
                        onManageEnrollment={(sec) => handleManageSectionEnrollment(sec, index)}
                        showEnrollmentButton
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <p className="mt-2 text-xs text-gray-500">
                可按「參加者名單」加入會員；若活動／場次尚未儲存，系統會先自動儲存再開名單。
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('events.modal.buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('events.modal.buttons.saving') : (effectiveIsCreate ? t('events.modal.buttons.create') : t('events.modal.buttons.save'))}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showEnrollmentModal && workingEvent?.id && (
        <CourseEnrollmentModal
          event={workingEvent}
          initialSectionId={enrollmentSectionId}
          onClose={() => {
            setShowEnrollmentModal(false);
            setEnrollmentSectionId('');
          }}
        />
      )}
    </div>
  );
};

export default EventModal;
